import { webApi } from '@mi-front/api-clients';
import { Group } from '@visx/group';
import React from 'react';
import { QUALITY_STATUS_COLORS, QUALITY_TIMELINE } from '../../consts';
import { clampTimeRangeToDomain } from '../../lib/quality-time-range';
import { TimelineTooltipPayload } from '../../lib/timeline-tooltip-types';
import { getTimelineBarPixelSpan } from '../lib/timeline-bar-pixel-span';
import { TimelineChartXScale } from '../lib/timeline-chart-scale-types';

export type TimelineStationRowProps = {
  stationId: string;
  stationRow: webApi.StreamQualityPerStationDto | undefined;
  y: number;
  rowBandHeight: number;
  innerWidth: number;
  xScale: TimelineChartXScale;
  visibleStart: Date;
  visibleEnd: Date;
  globalEnd: Date;
  onRowMouseEnter: () => void;
  onRowMouseLeave: () => void;
  showHover: (event: React.MouseEvent<SVGRectElement>, data: TimelineTooltipPayload) => void;
  hideTimelineTooltip: () => void;
  openIssueInLogger: (stationId: string, issueStart: Date) => void;
};

/**
 * One station band: row hit target, online baseline, issue and gap bars (clamped to visible window).
 */
export function TimelineStationRow({
  stationId,
  stationRow,
  y,
  rowBandHeight,
  innerWidth,
  xScale,
  visibleStart,
  visibleEnd,
  globalEnd,
  onRowMouseEnter,
  onRowMouseLeave,
  showHover,
  hideTimelineTooltip,
  openIssueInLogger,
}: TimelineStationRowProps) {
  const barY = QUALITY_TIMELINE.barVerticalInset;
  const barHeight = Math.max(1, rowBandHeight - 2 * QUALITY_TIMELINE.barVerticalInset);

  return (
    <Group top={y} onMouseEnter={onRowMouseEnter} onMouseLeave={onRowMouseLeave}>
      {/* Full row hit target so hover works in the inset strips above/below the bars. */}
      <rect x={0} y={0} width={innerWidth} height={rowBandHeight} fill="transparent" />
      <rect
        x={0}
        y={barY}
        width={innerWidth}
        height={barHeight}
        fill={QUALITY_STATUS_COLORS.online}
        fillOpacity={0.35}
      />
      {stationRow?.audioQualityIssues.map((issue, i) => {
        const startAt = new Date(issue.start);
        const endAt = new Date(issue.end);
        const clamped = clampTimeRangeToDomain(startAt, endAt, visibleStart, visibleEnd);
        if (!clamped) {
          return null;
        }
        const { startX, barWidth } = getTimelineBarPixelSpan(
          xScale,
          innerWidth,
          clamped.start,
          clamped.end,
        );
        return (
          <rect
            key={`issue-${stationId}-${i}`}
            x={startX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={
              issue.kind === webApi.AudioQualityIssueDtoKindEnum.silent
                ? QUALITY_STATUS_COLORS.issueSilent
                : QUALITY_STATUS_COLORS.issuePoorQuality
            }
            style={{ cursor: 'pointer' }}
            onClick={() => openIssueInLogger(stationId, startAt)}
            onMouseMove={(e) =>
              showHover(e, {
                type: 'issue',
                start: clamped.start,
                end: clamped.end,
                stationId,
                issue,
              })
            }
            onMouseLeave={hideTimelineTooltip}
          />
        );
      })}
      {stationRow?.streamGaps.map((gap, i) => {
        if (!gap.start) {
          return null;
        }
        const startAt = new Date(gap.start);
        const endAt = gap.end ? new Date(gap.end) : globalEnd;
        const clamped = clampTimeRangeToDomain(startAt, endAt, visibleStart, visibleEnd);
        if (!clamped) {
          return null;
        }
        const { startX, barWidth } = getTimelineBarPixelSpan(
          xScale,
          innerWidth,
          clamped.start,
          clamped.end,
        );
        return (
          <rect
            key={`gap-${stationId}-${gap.id}-${i}`}
            x={startX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={QUALITY_STATUS_COLORS.offline}
            style={{ cursor: 'pointer' }}
            onClick={() => openIssueInLogger(stationId, startAt)}
            onMouseMove={(e) =>
              showHover(e, {
                type: 'gap',
                start: clamped.start,
                end: clamped.end,
                stationId,
                gap,
              })
            }
            onMouseLeave={hideTimelineTooltip}
          />
        );
      })}
    </Group>
  );
}
